import { useEffect, useRef, useState } from 'react';
import './DevRenderOverlay.css';

let IS_DEV = false;
// PRODUCTION GUARD - Return null immediately in production
IS_DEV = process.env.NODE_ENV === 'development';

if (!IS_DEV || IS_DEV === null || IS_DEV === undefined) {
  IS_DEV = false;
}

// Lazy imports only in dev to avoid bundling in production
let clearApiCallRecords, logApiCallTotals;
let getSseThrottleMs;
let notifySseActivity, resetSseActivity, subscribeSse;

if (IS_DEV) {
  try {
    const apiClient = require('services/_infra/apiClient');
    clearApiCallRecords = apiClient.clearApiCallRecords;
    logApiCallTotals = apiClient.logApiCallTotals;

    getSseThrottleMs = require('../../hooks/utils/sseThrottle').getSseThrottleMs;

    const sseHelper = require('./sseActivityHelper');
    notifySseActivity = sseHelper.notifySseActivity;
    resetSseActivity = sseHelper.resetSseActivity;
    subscribeSse = sseHelper.subscribe;
  } catch (e) {
    console.warn('DevRenderOverlay: Failed to load dependencies', e);
  }
}

// Tab definitions for the dev overlay
const TABS = [
  {
    id: 'renders',
    label: 'Renders',
    icon: '🔄',
    tooltip: 'Component Render Counts - Track how often components re-render',
  },
  {
    id: 'api',
    label: 'API',
    icon: '📡',
    tooltip: 'API Call Tracker - Monitor API requests (click to copy)',
  },
  {
    id: 'sse',
    label: 'SSE',
    icon: '⏱️',
    tooltip: 'SSE Activity - View Server-Sent Events activity',
  },
];

// Wrapper component that returns null in production
export default function DevRenderOverlay() {
  if (!IS_DEV) return null;
  return <DevRenderOverlayInner />;
}

function DevRenderOverlayInner() {
  const [renderData, setRenderData] = useState([]);
  const [apiData, setApiData] = useState([]);
  const [hidden, setHidden] = useState(true);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [apiSummary, setApiSummary] = useState(null);
  const [sseActive, setSseActive] = useState(false);
  const [sseLastEvent, setSseLastEvent] = useState(null);
  const [sseCounts, setSseCounts] = useState({});
  const [activeTab, setActiveTab] = useState(null); // null = minimized bar only

  // Keyboard shortcut: Ctrl+Shift+D to toggle visibility
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setHidden((h) => !h);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const update = () => {
      try {
        // Render tracker
        if (window.__renderTracker && typeof window.__renderTracker.getAll === 'function') {
          const all = window.__renderTracker.getAll();
          // normalize both formats: [{component,count}] or [[component,count]]
          let arr = [];
          if (all.length && Array.isArray(all[0])) {
            arr = all.map(([component, count]) => ({ component, count }));
          } else {
            arr = all.map((item) => ({
              component: item.component || item[0],
              count: item.count || item[1] || 0,
            }));
          }
          arr.sort((a, b) => b.count - a.count);
          setRenderData(arr.slice(0, 20));
        } else if (window.__renderTracker && typeof window.__renderTracker.dump === 'function') {
          const arr = window.__renderTracker.dump();
          setRenderData(arr.slice(0, 20));
        } else {
          setRenderData([]);
        }

        // API tracker
        if (window.__apiCallTracker && typeof window.__apiCallTracker.getAll === 'function') {
          const all = window.__apiCallTracker.getAll();
          const arr = all.map(([key, count]) => ({ call: key, count }));
          arr.sort((a, b) => b.count - a.count);
          setApiData(arr.slice(0, 10));
        } else if (window.__apiCallTracker && typeof window.__apiCallTracker.dump === 'function') {
          const arr = window.__apiCallTracker.dump();
          setApiData(arr.slice(0, 10));
        } else {
          setApiData([]);
        }

        // API call summary
        try {
          const summary = logApiCallTotals();
          setApiSummary(summary);
        } catch (e) {
          // ignore if not available
        }
      } catch (err) {
        // ignore
      }
    };

    update();
    const id = setInterval(update, 1000);

    // Subscribe to SSE activity helper for immediate updates
    const unsub = subscribeSse((snapshot) => {
      setSseActive(Boolean(snapshot.active));
      setSseLastEvent(snapshot.lastEvent);
      setSseCounts(snapshot.counts || {});
    });

    // Expose a helper on window for quick testing in dev
    try {
      if (typeof window !== 'undefined') {
        window.__notifySseActivity = notifySseActivity;
      }
    } catch (e) {}

    return () => {
      clearInterval(id);
      unsub();
      try {
        if (typeof window !== 'undefined') delete window.__notifySseActivity;
      } catch (e) {}
    };
  }, []);

  // persist panel state in session for dev comfort
  const [panelState, setPanelState] = useState(() => {
    try {
      const s = window.sessionStorage.getItem('devOverlayPanelState');
      const defaultState = { barTop: 8, barRight: 8, freePosition: false };
      return s ? { ...defaultState, ...JSON.parse(s) } : defaultState;
    } catch (e) {
      return { barTop: 8, barRight: 8, freePosition: false };
    }
  });

  useEffect(() => {
    try {
      window.sessionStorage.setItem('devOverlayPanelState', JSON.stringify(panelState));
    } catch (e) {}
  }, [panelState]);

  // Drag state using refs to avoid stale closures
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartY = useRef(0);
  const dragStartTop = useRef(0);
  const dragStartRight = useRef(0);

  const handlePointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartY.current = e.clientY;
    dragStartTop.current = panelState.barTop;
    dragStartRight.current = panelState.barRight;

    // Capture pointer to ensure we get all events
    e.target.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const dx = e.clientX - dragStartX.current;
    const dy = e.clientY - dragStartY.current;

    // Calculate new positions
    let newTop = dragStartTop.current + dy;
    let newRight = dragStartRight.current - dx; // subtract because right increases leftward

    // Constrain to viewport
    newTop = Math.max(4, Math.min(newTop, window.innerHeight - 40));
    newRight = Math.max(4, Math.min(newRight, window.innerWidth - 100));

    setPanelState((prev) => ({ ...prev, barTop: newTop, barRight: newRight, freePosition: true }));
  };

  const handlePointerUp = (e) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    e.target.releasePointerCapture(e.pointerId);
  };

  // Hidden state - still listening for keyboard shortcut
  if (hidden) {
    return null;
  }

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  const toggleDock = () => {
    setPanelState((prev) => {
      // Toggle between right and left side, reset to edge
      const isRight = prev.barRight < window.innerWidth / 2;
      return {
        ...prev,
        barRight: isRight ? window.innerWidth - 100 : 8,
        freePosition: true,
      };
    });
  };

  const handleTabClick = (tabId) => {
    setActiveTab((prev) => (prev === tabId ? null : tabId));
  };

  // Render panel content based on active tab
  const renderPanelContent = () => {
    switch (activeTab) {
      case 'renders':
        return (
          <div className="dev-panel-content">
            <div className="dev-panel-header">
              <span>Render Counts</span>
              <button
                onClick={() => {
                  window.__renderTracker?.reset?.();
                  setRenderData([]);
                }}
                title="Reset render counts"
              >
                Reset
              </button>
            </div>
            <div className="dev-panel-body">
              {renderData.length === 0 && <div className="muted">No render data</div>}
              {renderData.map((row) => (
                <div key={row.component} className="dev-panel-row">
                  <span className="comp">{row.component}</span>
                  <span className="count">{row.count}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'api':
        return (
          <div className="dev-panel-content">
            <div className="dev-panel-header">
              <span>API Calls</span>
              <button
                onClick={() => {
                  window.__apiCallTracker?.reset?.();
                  setApiData([]);
                  try {
                    clearApiCallRecords();
                  } catch (e) {}
                  setApiSummary(null);
                }}
                title="Reset API counts"
              >
                Reset
              </button>
            </div>
            <div className="dev-panel-body">
              {apiSummary && (
                <>
                  <div className="dev-panel-stats">
                    <span>Unique: {apiSummary.uniqueKeys}</span>
                    <span>Total: {apiSummary.totalCalls}</span>
                  </div>
                  <div className="dev-panel-section-divider" />
                </>
              )}
              {apiData.length === 0 && <div className="muted">No API calls yet</div>}
              {apiData.map((row, idx) => (
                <div
                  key={row.call || idx}
                  className={`dev-panel-row clickable ${copiedIndex === idx ? 'copied' : ''}`}
                  onClick={() => {
                    copyToClipboard(row.call);
                    setCopiedIndex(idx);
                    setTimeout(() => setCopiedIndex(null), 1200);
                  }}
                >
                  <span className="comp">{row.call}</span>
                  <span className="count">{copiedIndex === idx ? '✓' : row.count}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'sse':
        return (
          <div className="dev-panel-content">
            <div className="dev-panel-header">
              <span>SSE Activity</span>
              <button
                onClick={() => {
                  try {
                    resetSseActivity();
                  } catch (e) {}
                  setSseCounts({});
                  setSseLastEvent(null);
                  setSseActive(false);
                }}
                title="Clear SSE activity"
              >
                Clear
              </button>
            </div>
            <div className="dev-panel-body">
              {!Object.keys(sseCounts || {}).length && (
                <div className="muted">No SSE events recorded</div>
              )}
              {Object.entries(sseCounts || {})
                .sort((a, b) => b[1] - a[1])
                .map(([key, c]) => (
                  <div key={key} className="dev-panel-row">
                    <span className="comp">{key}</span>
                    <span className="count">{c}</span>
                  </div>
                ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="dev-overlay-container"
      style={{
        top: panelState.barTop,
        right: panelState.barRight,
      }}
    >
      {/* Slim draggable bar */}
      <div className="dev-bar">
        <div
          className="dev-bar-drag-handle"
          data-tooltip="Drag to move toolbar anywhere on screen"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{ touchAction: 'none' }}
        >
          ⋮⋮
        </div>

        {/* Quick stats in the bar */}
        <div className="dev-bar-stats">
          <span
            className={`sse-indicator ${sseActive ? 'active' : ''}`}
            data-tooltip={`SSE Status: ${sseActive ? 'Active' : 'Idle'}${sseLastEvent ? ` | Last event: ${sseLastEvent}` : ''}`}
          >
            ●
          </span>
          <span
            className="stat"
            data-tooltip={`SSE Throttle: ${getSseThrottleMs()}ms delay between SSE updates`}
          >
            SSE:{getSseThrottleMs()}ms
          </span>
          <span
            className="stat"
            data-tooltip={
              renderData.length > 0
                ? `Top Render: ${renderData[0]?.component} rendered ${renderData[0]?.count} times`
                : 'No render data yet'
            }
          >
            {renderData.length > 0 ? `R:${renderData[0]?.count || 0}` : ''}
          </span>
        </div>

        {/* Tab buttons */}
        <div className="dev-bar-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              className={`dev-bar-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabClick(tab.id)}
              data-tooltip={tab.tooltip}
            >
              {tab.icon}
            </button>
          ))}
        </div>

        {/* Controls */}
        <div className="dev-bar-controls">
          <button onClick={toggleDock} data-tooltip="Toggle toolbar between left and right side">
            ⇄
          </button>
          <button
            onClick={() => setHidden(true)}
            data-tooltip="Hide Dev Toolbar (Press Ctrl+Shift+D to show again)"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Panel that slides out when a tab is active */}
      {activeTab && <div className="dev-panel">{renderPanelContent()}</div>}
    </div>
  );
}
